import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Phone, CheckCircle, Music, TrendingUp, Eye, DollarSign, Heart, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const ARTIST_TYPES = ["Singer / Vocalist", "Rapper / MC", "Music Producer", "Lyricist / Songwriter", "Music Video Director", "DJ / Electronic Artist", "Composer", "Band / Group", "Independent Artist", "Content Creator", "Music Label / Manager"];
const GENRES = ["Punjabi", "Hindi", "Indie", "Hip-Hop / Rap", "Pop", "Classical / Sufi", "Devotional", "Electronic / EDM", "Folk / Regional", "Haryanvi", "Bhojpuri", "Ghazal", "Other"];
const NEEDS = ["YouTube Views (Google Ads)", "Instagram Reels & Fan Page Promotion", "Get My Song on Trending Charts", "Spotify / JioSaavn / Apple Music Promotion", "Song Distribution to 255+ Platforms", "Content ID & Royalty Setup", "Brand Deals & Sponsorships", "Influencer / Creator Reels for My Song", "Music Video Launch Campaign", "PR & Media Coverage", "Artist Branding & Press Kit", "Playlist Pitching (Editorial)", "Train Me — I Want to Learn", "Full Package — Everything Above"];
const BUDGETS = ["Under 25,000", "25,000 - 50,000", "50,000 - 1,00,000", "1,00,000 - 3,00,000", "3,00,000+", "Let's discuss"];

export default function ArtistsPage() {
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [f, setF] = useState({ name: "", artist_name: "", email: "", phone: "", artist_type: "", genre: "", need: "", budget: "", social_links: "", details: "" });
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) { toast.error("Name and phone number are required."); return; }
    setSending(true);
    try { await axios.post(`${API}/artist-inquiry`, f); setDone(true); toast.success("Done!"); window.scrollTo(0, 0); }
    catch { toast.error("Something went wrong. Call us directly."); }
    setSending(false);
  };

  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-md">
        <Music className="w-10 h-10 text-white mx-auto" />
        <h1 className="text-2xl md:text-3xl tracking-tight mt-8">We got it.</h1>
        <p className="text-zinc-400 text-xs mt-4 tracking-wider">Someone from our artist team will call you within 24 hours.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
          <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I'm%20an%20artist%20and%20I%20just%20filled%20the%20form" target="_blank" rel="noopener noreferrer" data-testid="artist-success-whatsapp"
            className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-3">
            Whatsapp us <ArrowRight className="w-4 h-4" />
          </a>
          <a href="tel:+916239547599" className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center inline-flex items-center justify-center gap-2">
            <Phone className="w-3 h-3" /> Call now
          </a>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">

      {/* ━━━ HERO ━━━ */}
      <section data-testid="artist-hero" className="pt-32 pb-16 px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="artist-back-link">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Song Promotion & Artist Marketing — India, Dubai, Canada</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] max-w-5xl">
            You make the music.
            <br />
            <span className="text-zinc-500">We make sure the world hears it.</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-10 max-w-2xl leading-relaxed tracking-wider">
            YouTube ads. Trending charts. Spotify playlists. Brand deals. Distribution to 255+ platforms. We've done it for Karan Aujla's team, for Nazme Kamal, for labels like Believe, Warner, and Universal. Now we're doing it for you.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <a href="#artist-form" data-testid="artist-hero-cta"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Get started <ArrowRight className="w-4 h-4" />
            </a>
            <a href="https://wa.me/919041344420?text=Hi%2C%20I%20need%20song%20promotion%20help" target="_blank" rel="noopener noreferrer"
              className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors">
              Whatsapp us
            </a>
          </div>
        </motion.div>
      </section>

      {/* ━━━ TRUST BAR ━━━ */}
      <section className="px-6 md:px-16 lg:px-28 py-8 border-y border-white/10">
        <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
          <p className="text-[10px] text-zinc-600 tracking-[0.2em]">Backend partners:</p>
          {["Believe Music", "Warner Music", "Universal Music", "Virgin Music", "Sony Music", "Saregama"].map(l => (
            <p key={l} className="text-[10px] text-zinc-500 tracking-wider">{l}</p>
          ))}
        </div>
      </section>

      {/* ━━━ WHO IS THIS FOR — Buyer Self-Identification ━━━ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Is this for you?</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">If any of this sounds like you, keep reading.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-12 bg-white/10">
          {[
            { title: "I just recorded my first song", desc: "You have a track ready. Maybe a video too. But you don't know how to get it in front of people. You need views, streams, and someone who won't waste your budget." },
            { title: "I'm getting views but I've plateaued", desc: "Your first few songs did okay — maybe 50K-200K views. But you can't break past that ceiling. You need trending, editorial playlists, and a real growth strategy." },
            { title: "I'm an established artist or label", desc: "You need backend infrastructure — distribution to 255+ platforms, Content ID, CMS, royalty management, editorial playlist pitching. Professional-grade, not amateur." },
            { title: "I manage artists and need a partner", desc: "You need a reliable agency that handles promotion, distribution, and creator booking while you focus on talent. White-label or co-branded — we do both." }
          ].map(p => (
            <div key={p.title} className="bg-black p-8">
              <p className="text-xs tracking-wider">{p.title}</p>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider">{p.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ━━━ SERVICES — Organized by What Buyer Needs ━━━ */}
      <section data-testid="artist-services" className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">What we do</p>
          <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">Six ways we grow your music career.</h2>
        </motion.div>

        {/* Service 1: Views */}
        <div className="mt-16 bg-[#080808] border border-white/10 p-8 md:p-12">
          <div className="flex items-start gap-4">
            <Eye className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm md:text-base tracking-wider">Get real YouTube views through Google Ads</h3>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider max-w-3xl">
                We run display ads and skippable ads specifically for music videos — not bots, not fake views. Real people watching your song through Google's ad network. We're one of the largest YouTube ad operators in North India with direct Google invoices and billing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-6 border-t border-white/5">
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Targeting</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">Geography (Punjab, Haryana, Delhi, Mumbai, Canada, UK, UAE), age, language, platform. If your Punjabi song needs to hit Amritsar, Jalandhar, and Brampton — we target exactly that.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Why us</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">Lowest CPV (cost per view) rates in the market. We've been running these campaigns since before most agencies even existed. Direct Google invoices — 100% legitimate.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Ad types</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">YouTube display ads, skippable in-stream ads, YouTube premiere campaigns, thumbnail A/B testing, SEO-optimized titles and descriptions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service 2: Trending */}
        <div className="mt-4 bg-[#080808] border border-white/10 p-8 md:p-12">
          <div className="flex items-start gap-4">
            <TrendingUp className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm md:text-base tracking-wider">Get your song on India's Trending Charts</h3>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider max-w-3xl">
                Audio seeding + reel promotion + fan page activation = trending. We plan and execute the exact combination that pushes songs into India's Top 50, even Top 5.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-6 border-t border-white/5">
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Audio seeding</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">Coordinated rollout across 100-200 lyrical pages, fan pages, Bollywood pages, edit pages (dialogue-based), and dance reels. 5-7 days of sustained execution.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Our page network</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">We've built fan pages for artists like Neha Kakkar, Bharti Singh, Tanya Mittal, Jasmine Singh — pages with millions of organic reach that we use to seed your audio.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Result</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">When the seeding volume hits critical mass, the algorithm picks it up organically. Your song starts trending — Top 50 to Top 5 on India charts, depending on the campaign scale.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service 3: Distribution */}
        <div className="mt-4 bg-[#080808] border border-white/10 p-8 md:p-12">
          <div className="flex items-start gap-4">
            <Music className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm md:text-base tracking-wider">Distribute your song to 255+ platforms in 48 hours</h3>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider max-w-3xl">
                Direct tie-ups with Believe, Warner, Universal, Virgin. We have portal access — no middlemen. Your song goes live on Spotify, Apple Music, JioSaavn, Gaana, Amazon Music, YouTube Music, Wynk, and 255+ stores worldwide.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-6 border-t border-white/5">
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Your royalty</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">You keep 80%. We charge 20% (covers publisher fee + our management). Transparent, simple, no hidden costs. Royalty reports every quarter.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">What's included</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">Song upload, metadata setup, ISRC codes, Content ID registration, CMS (Content Management System), catalog management, and ongoing changes whenever you need them.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Playlist pitching</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">For artists with strong streaming, we pitch to editorial playlists on Spotify, JioSaavn, and Apple Music. Need your song 14 days before release. Editorial placement = organic reach explosion.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service 4: Creator/Influencer */}
        <div className="mt-4 bg-[#080808] border border-white/10 p-8 md:p-12">
          <div className="flex items-start gap-4">
            <Heart className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm md:text-base tracking-wider">Influencer reels & creator marketing for your song</h3>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider max-w-3xl">
                Dance creators, hip-hop creators, Punjabi creators, comedy creators. We plan which creators match your song's vibe, handle all booking and negotiation, and deliver the reels. Or bring your own list — we'll book them on your behalf.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8 pt-6 border-t border-white/5">
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">How it works</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">You give us a budget. We plan the campaign — which creators, how many reels, timing, content angles. Or you tell us exactly who you want. Either way, we handle outreach, pricing negotiation, briefing, content approval, and posting. Commission-based, fully transparent.</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Our network</p>
                  <p className="text-zinc-400 text-[10px] tracking-wider leading-relaxed">Dance creators, lip-sync creators, dialogue/edit creators, hip-hop performers, Punjabi content creators, lifestyle influencers. From 10K to 10M follower creators — all categories, all budgets.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service 5: Brand Deals */}
        <div className="mt-4 bg-[#080808] border border-white/10 p-8 md:p-12">
          <div className="flex items-start gap-4">
            <DollarSign className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm md:text-base tracking-wider">Get brand deals & sponsorships</h3>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider max-w-3xl">
                Most artists don't get brand deals because nobody pitches them properly. We create your media kit, build your pitch deck with real engagement data, identify matching brands (fashion, FMCG, tech, audio, beverages), and handle the entire negotiation. You show up, create content, get paid.
              </p>
            </div>
          </div>
        </div>

        {/* Service 6: Learn */}
        <div className="mt-4 bg-[#080808] border border-white/10 p-8 md:p-12">
          <div className="flex items-start gap-4">
            <Shield className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm md:text-base tracking-wider">Learn to do it yourself</h3>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider max-w-3xl">
                Don't want to outsource? We'll teach you. YouTube ads, Instagram growth, Spotify pitching, brand deal negotiations, music business fundamentals. Walk away with skills, not dependency. We want you to grow — with or without us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ WHO WE'VE WORKED WITH ━━━ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        data-testid="artist-clients" className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Who trusts us</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">From the biggest names to the newest faces.</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-12 bg-white/10">
          {[
            { name: "Karan Aujla", desc: "Backend promotion for tracks crossing millions of views. When the biggest names in Punjabi music need their numbers, we're in the backend making it happen." },
            { name: "Nazme Kamal", desc: "Complete digital management — song promotion, reel promotion, content strategy, full digital presence. End-to-end, from scratch." },
            { name: "Akanksha Khanna", desc: "Digital projects for Akanksha Khanna's company — wife of Gaurav Khanna (Bigg Boss winner). Brand presence, content, and campaigns across verticals." },
            { name: "Prince (Punjab)", desc: "New artist. Latest song launched this week with full YouTube ads, Instagram reel campaign, and launch strategy. Fresh talent, full support." }
          ].map(a => (
            <div key={a.name} className="bg-black p-8">
              <p className="text-xs tracking-wider">{a.name}</p>
              <p className="text-zinc-500 text-[10px] mt-2 leading-relaxed tracking-wider">{a.desc}</p>
            </div>
          ))}
        </div>

        <p className="text-zinc-500 text-[10px] mt-10 tracking-wider max-w-2xl leading-relaxed">
          We also work in the backend for Pixil Digital, DJ Film, and major labels including Believe Music, Warner Music, Universal Music, Virgin Music, Sony Music, and Saregama — handling their digital distribution, CMS, Content ID, and catalog management through direct portal access.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-10 border-t border-white/10">
          {[
            { n: "50M+", l: "YouTube views delivered" },
            { n: "500+", l: "Songs distributed & promoted" },
            { n: "255+", l: "Platforms we distribute to" },
            { n: "48hrs", l: "Song live everywhere" }
          ].map(s => (
            <div key={s.l}>
              <p className="text-2xl md:text-3xl">{s.n}</p>
              <p className="text-[10px] text-zinc-600 mt-2 tracking-[0.2em]">{s.l}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ━━━ CASE STUDIES (Compact) ━━━ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        data-testid="artist-case-studies" className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Results</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">What happened when artists trusted us.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-12 bg-white/10">
          {[
            { title: "Punjabi Singer", before: "2,300 YouTube subscribers. 3 songs under 15K views total.", after: "2.8M views on 4th track. 204K subscribers. Spotify 45K monthly listeners. First brand deal landed." },
            { title: "Chandigarh Rapper", before: "First video stuck at 50K views in 3 months. Thought that was the ceiling.", after: "3rd video crossed 5.2M views in first month. 95K Instagram followers. Rolling Stone India feature." },
            { title: "Music Director", before: "Made beats for 10M+ view songs but zero personal brand. 500 Instagram followers.", after: "3 brand deals in first quarter (headphones, streaming app, studio gear). 22K followers." },
            { title: "Hindi Indie Singer", before: "10K views per video. 3K Instagram. Stuck in the 'indie bubble.'", after: "2.1M views in 30 days. 12 Spotify playlists. Co-write opportunity with Bollywood composer." }
          ].map((cs, i) => (
            <div key={i} className="bg-black p-8">
              <p className="text-xs tracking-wider">{cs.title}</p>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.15em]">Before</p>
                  <p className="text-zinc-500 text-[10px] tracking-wider leading-relaxed">{cs.before}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 tracking-[0.15em]">After</p>
                  <p className="text-zinc-300 text-[10px] tracking-wider leading-relaxed">{cs.after}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ━━━ HOW IT WORKS ━━━ */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">How it works</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">Three steps. That's it.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 bg-white/10">
          {[
            { n: "01", title: "Tell us about your music", desc: "Fill the form, WhatsApp us, or call. Share your song, your socials, your budget. We'll listen to your track and study your audience — free, no charge." },
            { n: "02", title: "We build your campaign", desc: "Custom plan: how many YouTube ad views, which creators for reels, audio seeding strategy, distribution setup, playlist pitching timeline. Real numbers. You approve everything." },
            { n: "03", title: "We execute. You create.", desc: "Ads go live. Reels get posted. Song goes to 255+ platforms. Playlists get pitched. We send weekly reports — you see exactly where every rupee went." }
          ].map(s => (
            <div key={s.n} className="bg-black p-10">
              <p className="text-zinc-700 text-xl">{s.n}</p>
              <p className="text-sm tracking-wider mt-4">{s.title}</p>
              <p className="text-zinc-500 text-[10px] mt-3 leading-relaxed tracking-wider">{s.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ━━━ CTA FORM ━━━ */}
      <section id="artist-form" data-testid="artist-form-section" className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Get started</p>
          <h2 className="text-2xl md:text-3xl tracking-tight">Tell us what you need. We'll call you within 24 hours.</h2>
        </motion.div>
        <form onSubmit={submit} className="mt-12 max-w-3xl space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Inp label="Your name *" id="artist-name" val={f.name} set={v => u("name", v)} ph="Real name" />
            <Inp label="Artist / Stage name" id="artist-stage-name" val={f.artist_name} set={v => u("artist_name", v)} ph="What people know you as" />
            <Inp label="Phone *" id="artist-phone" val={f.phone} set={v => u("phone", v)} ph="+91 XXXXX XXXXX" />
            <Inp label="Email" id="artist-email" val={f.email} set={v => u("email", v)} ph="you@email.com" type="email" />
          </div>
          <Chips label="I am a..." items={ARTIST_TYPES} active={f.artist_type} set={v => u("artist_type", v)} prefix="type" />
          <Chips label="My genre" items={GENRES} active={f.genre} set={v => u("genre", v)} prefix="genre" />
          <Chips label="I need help with" items={NEEDS} active={f.need} set={v => u("need", v)} prefix="need" />
          <Chips label="Budget (INR per month or per project)" items={BUDGETS} active={f.budget} set={v => u("budget", v)} prefix="budget" />
          <div>
            <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Your links (YouTube, Instagram, Spotify)</label>
            <Textarea data-testid="artist-links" value={f.social_links} onChange={e => u("social_links", e.target.value)}
              placeholder="Drop all your links here..." rows={2}
              className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Anything else we should know</label>
            <Textarea data-testid="artist-details" value={f.details} onChange={e => u("details", e.target.value)}
              placeholder="Where are you in your career? What's your biggest challenge?" rows={3}
              className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
          </div>
          <Button type="submit" disabled={sending} data-testid="artist-form-submit"
            className="bg-white text-black hover:bg-zinc-200 rounded-none px-10 py-4 text-xs tracking-[0.2em] h-auto disabled:opacity-50 w-full md:w-auto">
            {sending ? "Sending..." : "Get started"} {!sending && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <p className="text-[10px] text-zinc-600 tracking-wider">Free consultation. No contracts. Your music stays yours.</p>
        </form>
      </section>

      {/* ━━━ SEO ━━━ */}
      <section className="py-12 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="flex flex-wrap gap-2">
          {[
            "song promotion company India", "Punjabi song promotion", "Hindi song promotion agency",
            "YouTube views for music video", "music video promotion India", "best song promotion company",
            "Google Ads for music video", "YouTube display ads for songs", "music marketing agency India",
            "Spotify playlist placement India", "how to get brand deals as artist",
            "independent artist promotion", "song promotion price India",
            "Instagram reels promotion for songs", "music distribution company India",
            "audio seeding service India", "top trending India chart promotion",
            "get song on trending India", "fan page promotion music",
            "influencer marketing for musicians India", "creator booking for songs",
            "cheapest YouTube ads for songs India", "Neha Kakkar fan page promotion",
            "music reel promotion agency", "song distribution 255 platforms",
            "Content ID setup for artists", "Believe Music distribution India",
            "Warner Music backend services", "editorial playlist pitching service",
            "Karan Aujla promotion team", "Punjabi artist management agency",
            "music label backend services", "ISRC code registration",
            "artist press kit design", "music PR agency India",
            "rapper marketing India", "singer promotion package India",
            "music promotion company Dubai", "music promotion company Chandigarh",
            "Haryanvi song promotion", "Bhojpuri song promotion company",
            "JioSaavn promotion", "Apple Music playlist India",
            "YouTube monetization for artists", "how to grow as independent artist India",
            "hire someone to promote my song", "need help getting YouTube views on my music video",
            "looking for agency to promote my Punjabi song", "who can help me get brand deals as artist",
            "agency that can make my song go viral", "need someone to handle my music marketing",
            "company to help promote my Hindi song on YouTube", "looking for music promotion expert",
            "hire agency for Instagram reel promotion for my song", "need professional music video promotion",
            "someone to get my song on Spotify playlists", "agency that handles artist promotion end to end",
            "how to hire a music marketing agency", "looking for someone to distribute my song worldwide",
            "need help with YouTube premiere campaign for my track", "hire music PR agency for my release",
            "agency that can get me brand collaborations as singer", "who promotes songs for new artists in India"
          ].map(kw => (
            <span key={kw} className="text-[10px] text-zinc-700 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>
          ))}
        </div>
      </section>
    </div>
  );
}

function Inp({ label, id, val, set, ph, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">{label}</label>
      <Input data-testid={id} type={type} value={val} onChange={e => set(e.target.value)}
        placeholder={ph} className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
    </div>
  );
}

function Chips({ label, items, active, set, prefix }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.15em] text-zinc-500 mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(item => (
          <button key={item} type="button" data-testid={`${prefix}-${item.slice(0, 12)}`} onClick={() => set(item)}
            className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${active === item ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}
